import React, { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Switch, Tag } from 'antd';
import { debounce } from 'lodash';
import { observer } from 'mobx-react-lite';
import { ReactComponent as SaveOutlined } from 'assets/icons/save.svg';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import SelectState from 'common/components/form-elements/select-state/select-state';
import { GuestType } from 'common/enums/guest-type.enum';
import { getGuestTypeValue } from 'common/helpers/guest.helper';
import { isServiceGuest } from 'common/helpers/guest-type.helpers';
import { guestTypeColorMap } from 'common/list-configs/guest-list.config';
import { IModal } from 'common/models/model.interface';
import citationService from 'common/services/citation-service/citation.service';
import { IEditCitationLicensePlate } from 'common/services/citation-service/citation.service.types';
import residentsService from 'common/services/residents-service/residents.service';
import { IGuestRead, IResidentAddressBaseRead } from 'common/services/residents-service/residents.service.types';
import { notification } from 'common/utils/notification';
import { addEditVehicleValidation } from '../../eiq-manage-residents/components/info-block/vehicles/add-edit-vehicle-modal/add-edit-vehicle-modal.validation';
import { citationOwnerTypeOptions } from '../citations.config';

interface EditCitationModalProps {
  citationId: number;
  licenseState: string | null;
  licenseNumber: string | null;
  ownerType?: number;
}

interface EditCitationFormValues {
  selectedPlateKey?: string;
  licenseState?: string;
  licenseNumber?: string;
  ownerType?: number;
}

interface CitationPlateOption {
  key: string;
  label: ReactNode;
  item: IEditCitationLicensePlate;
}

const ResidentTag = () => (
  <Tag className="eiq-tag" color="#C1FFEA" style={{ color: '#292B2E', marginRight: 0 }}>
    Resident
  </Tag>
);

const GuestTypeTag: FC<{ type: GuestType }> = ({ type }) => (
  <Tag className="eiq-tag" color={guestTypeColorMap[type]} style={{ color: '#292B2E', marginRight: 0 }}>
    {getGuestTypeValue(type)}
  </Tag>
);

const getGuestName = (guest: IGuestRead): string => {
  if (isServiceGuest(guest.type)) {
    return guest.companyName || '';
  }

  return [guest.firstName, guest.lastName].filter(Boolean).join(' ');
};

const includesSearch = (licenseNumber: string, search: string): boolean => {
  return licenseNumber.toLowerCase().includes(search.toLowerCase());
};

const buildPlateOptions = (residentAddresses: IResidentAddressBaseRead[], search: string): CitationPlateOption[] => {
  return residentAddresses.flatMap((address) => {
    const residentName = [address.resident.firstName, address.resident.lastName].filter(Boolean).join(' ');
    const residentId = address.resident.id;
    const addressId = address.address.id;

    const residentVehicleOptions = address.vehicles
      .filter((vehicle) => includesSearch(vehicle.licenseNumber, search))
      .map<CitationPlateOption>((vehicle, vehicleIndex) => ({
        key: `resident-${residentId}-${vehicle.licenseState}-${vehicle.licenseNumber}-${vehicleIndex}`,
        label: (
          <span>
            {residentName} <ResidentTag /> {vehicle.licenseNumber} ({vehicle.licenseState})
          </span>
        ),
        item: {
          residentId,
          addressId,
          number: vehicle.licenseNumber,
          state: vehicle.licenseState,
          guestId: null,
        },
      }));

    const guestVehicleOptions = address.guests.flatMap((guest) =>
      guest.carLicensePlates
        .filter((licensePlate) => includesSearch(licensePlate.number, search))
        .map<CitationPlateOption>((licensePlate, licensePlateIndex) => ({
          key: `guest-${residentId}-${guest.id}-${licensePlate.state}-${licensePlate.number}-${licensePlateIndex}`,
          label: (
            <span>
              {residentName}, Guest: {getGuestName(guest)} <GuestTypeTag type={guest.type as GuestType} /> {licensePlate.number} (
              {licensePlate.state})
            </span>
          ),
          item: {
            residentId,
            number: licensePlate.number,
            state: licensePlate.state,
            guestId: guest.id,
          },
        })),
    );

    return [...residentVehicleOptions, ...guestVehicleOptions];
  });
};

const EditCitationModal: FC<IModal<EditCitationModalProps, boolean>> = observer(({ isOpen, onClose, title, initData }) => {
  const [form] = Form.useForm<EditCitationFormValues>();
  const [options, setOptions] = useState<CitationPlateOption[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const requestIdRef = useRef(0);

  const searchLicensePlates = useCallback(
    debounce(async (value = '') => {
      const requestId = ++requestIdRef.current;
      const filter = value ? `residentLicensePlateNumber:^${value} OR guestLicensePlateNumber:^${value}` : null;

      setIsLoadingOptions(true);
      try {
        const result = await residentsService.getResidentAddressesDetails(null, filter, null);
        if (requestId === requestIdRef.current) {
          setOptions(buildPlateOptions(result.items, value));
        }
      } catch {
        if (requestId === requestIdRef.current) {
          setOptions([]);
          notification.error({ message: 'License plate search failed.' });
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoadingOptions(false);
        }
      }
    }, 350),
    [],
  );

  useEffect(() => {
    return () => searchLicensePlates.cancel();
  }, [searchLicensePlates]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    form.setFieldsValue({
      licenseNumber: initData?.licenseNumber ?? undefined,
      licenseState: initData?.licenseState ?? undefined,
      ownerType: initData?.ownerType,
    });
  }, [form, initData, isOpen]);

  const hideModal = (isSuccess: boolean) => {
    requestIdRef.current += 1;
    searchLicensePlates.cancel();
    form.resetFields();
    setOptions([]);
    setIsSearchMode(false);
    setIsLoadingOptions(false);
    onClose?.(isSuccess);
  };

  const onFinish = async (values: EditCitationFormValues) => {
    if (!initData?.citationId) {
      notification.error({ message: 'Citation id is missing.' });
      return;
    }

    let model: IEditCitationLicensePlate;

    if (isSearchMode) {
      const selectedOption = options.find((option) => option.key === values.selectedPlateKey);
      if (!selectedOption) {
        notification.error({ message: 'Select a license plate from the search results.' });
        return;
      }
      model = selectedOption.item;
    } else {
      if (!values.licenseState || !values.licenseNumber || values.ownerType === undefined) {
        notification.error({ message: 'License plate, state, and type are required.' });
        return;
      }

      model = {
        state: values.licenseState,
        number: values.licenseNumber,
        ownerType: values.ownerType,
      };
    }

    await citationService.editLicensePlate(initData.citationId, model);
    hideModal(true);
  };

  return (
    <Modal centered title={title} width={550} open={isOpen} wrapClassName="custom-modal" closable={false} footer={null}>
      <FormWrapper form={form} onFinish={onFinish} layout="vertical">
        <Space className="full-width" size={13}>
          <Form.Item label="Search existing license plate">
            <Switch checked={isSearchMode} onChange={setIsSearchMode} />
          </Form.Item>
        </Space>

        {isSearchMode ? (
          <Space className="full-width">
            <Form.Item
              name="selectedPlateKey"
              label="License Plate Number"
              rules={[{ required: true, message: 'Please select a license plate.' }]}>
              <Select
                placeholder="Search by license number"
                defaultActiveFirstOption={false}
                showSearch
                allowClear
                loading={isLoadingOptions}
                onSearch={searchLicensePlates}
                filterOption={false}
                onDropdownVisibleChange={(dropdownOpen) => dropdownOpen && options.length === 0 && searchLicensePlates()}
                options={options.map((option) => ({ label: option.label, value: option.key }))}
              />
            </Form.Item>
          </Space>
        ) : (
          <Space className="full-width">
            <Form.Item name="licenseNumber" label="License Plate" rules={addEditVehicleValidation.licenseNumber}>
              <Input name="licenseNumber" placeholder="Enter license plate" />
            </Form.Item>
            <Form.Item name="licenseState" label="License State" rules={addEditVehicleValidation.licenseState}>
              <SelectState placeholder="Select a license state" />
            </Form.Item>
            <Form.Item name="ownerType" label="Type" rules={[{ required: true, message: 'Type is required.' }]}>
              <Select options={citationOwnerTypeOptions} placeholder="Select a type" />
            </Form.Item>
          </Space>
        )}

        <Space className="footer">
          <Button type="link" onClick={() => hideModal(false)}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
            Save
          </Button>
        </Space>
      </FormWrapper>
    </Modal>
  );
});

export default EditCitationModal;
